import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { NavController } from '@ionic/angular';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ServiceWorkerService implements OnDestroy {
  private updateSubscription: Subscription | null = null;
  private readonly DEBUG = true; // Toggle debugging

  constructor(
    private update: SwUpdate,
    private zone: NgZone,
    private navCtrl: NavController
  ) {}

  /**
   * Initialize service worker updates
   */
  async init() {
    try {
      if (!this.update.isEnabled) {
        this.logDebug('Service Worker is not enabled');
        return;
      }

      await this.registerServiceWorker();
      this.subscribeForUpdates();
      await this.checkForUpdates();
    } catch (error) {
      this.logError('Failed to initialize service worker', error);
    }
  }

  /**
   * Register service worker
   */
  private async registerServiceWorker(): Promise<void> {
    try {
      if ('serviceWorker' in navigator) {
        await navigator.serviceWorker.register('/ngsw-worker.js');
        this.logDebug('Service Worker registered successfully');
      }
    } catch (error) {
      this.logError('Service Worker registration failed', error);
      throw error;
    }
  }

  /**
   * Subscribe to version updates
   */
  private subscribeForUpdates(): void {
    if (this.updateSubscription) {
      this.logDebug('Update subscription already active');
      return;
    }

    if (!this.update.isEnabled) {
      this.logDebug('Service Worker updates are not enabled');
      return;
    }

    try {
      this.updateSubscription = this.update.versionUpdates.subscribe({
        next: (evt) => this.handleVersionEvent(evt),
        error: (error) => {
          this.logError('Error in version updates subscription', error);
          this.unsubscribeFromUpdates();
        },
        complete: () => {
          this.logDebug('Version updates subscription completed');
        }
      });
    } catch (error) {
      this.logError('Failed to subscribe to updates', error);
    }
  }

  /**
   * Handle version update events
   */
  private handleVersionEvent(evt: any): void {
    console.log(evt)
    switch (evt.type) {
      case 'VERSION_DETECTED':
        this.logDebug(`Downloading new app version: ${evt.version.hash}`);
        break;
      case 'VERSION_READY':
        this.logDebug(`Current app version: ${evt.currentVersion.hash}`);
        this.logDebug(`New app version ready for use: ${evt.latestVersion.hash}`);
        this.updateApp();
        break;
      case 'VERSION_INSTALLATION_FAILED':
        this.logError(
          `Failed to install app version '${evt.version.hash}'`,
          evt.error
        );
        break;
      default:
        this.logDebug(`Unhandled version event type: ${evt.type}`);
    }
  }

  /**
   * Check for available updates
   */
  async checkForUpdates(): Promise<void> {
    if (!this.update.isEnabled) {
      this.logDebug('Service Worker not enabled');
      return;
    }

    try {
      const isUpdated = await this.update.checkForUpdate();
      this.logDebug('Update check completed:', isUpdated);
    } catch (error) {
      this.logError('Failed to check for updates', error);
    }
  }

  /**
   * Update the application
   */
  private async updateApp(): Promise<void> {
    try {
      await this.update.activateUpdate();
      this.zone.run(async () => {
        try {
          await this.navCtrl.navigateRoot('public-landing');
          this.logDebug('Navigation completed, reloading application...');
          window.location.reload();
        } catch (error) {
          this.logError('Navigation failed during update', error);
        }
      });
    } catch (error) {
      this.logError('Failed to activate update', error);
    }
  }

  /**
   * Unsubscribe from updates
   */
  private unsubscribeFromUpdates(): void {
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
      this.updateSubscription = null;
      this.logDebug('Unsubscribed from updates');
    }
  }

  /**
   * Check if subscription is active
   */
  isSubscriptionActive(): boolean {
    return this.updateSubscription !== null;
  }

  /**
   * Debug logging
   */
  private logDebug(...args: any[]): void {
    if (this.DEBUG) {
      console.log('[ServiceWorker]', ...args);
    }
  }

  /**
   * Error logging
   */
  private logError(message: string, error?: any): void {
    console.error('[ServiceWorker] ' + message, error);
  }

  /**
   * Cleanup on destroy
   */
  ngOnDestroy(): void {
    this.unsubscribeFromUpdates();
  }
}